/**
 * Map Pack vs Organic trend chart — Recharts dual-line, reversed Y axis (lower = better).
 * Pure presentational; expects a chronological `series` of {label, avg_organic, avg_local}.
 */
import React from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { MapPin } from "lucide-react";

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl shadow-sm px-3 py-2 text-xs">
      <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 font-mono-tight">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[#4a5568]">{p.name}</span>
          <span className="ml-auto text-[#1a202c] font-semibold">{p.value == null ? "—" : `#${p.value}`}</span>
        </div>
      ))}
    </div>
  );
}

export default function LocalPackTrendChart({ series }) {
  return (
    <div className="card-soft" data-testid="local-trend-chart">
      <div className="px-5 py-4 border-b border-[#edf2f7] flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
            <MapPin className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Map Pack vs Organic</div>
            <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Average position across tracked keywords</h3>
          </div>
        </div>
        <div className="text-[10px] text-[#a0aec0] font-mono-tight uppercase tracking-widest">lower = better</div>
      </div>
      {series.length < 2 ? (
        <div className="h-[240px] flex items-center justify-center text-xs text-[#a0aec0] font-mono-tight px-5">
          Need at least 2 rank snapshots to plot the trend. Run &ldquo;Check ranks now&rdquo; on the Rank tracking tab twice.
        </div>
      ) : (
        <div className="p-3 h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a0aec0" }} stroke="#cbd5e0" />
              <YAxis reversed allowDecimals={false} tick={{ fontSize: 10, fill: "#a0aec0" }} stroke="#cbd5e0" domain={[1, "auto"]} />
              <Tooltip content={<TrendTooltip />} />
              <Line
                type="monotone"
                dataKey="avg_organic"
                name="Organic"
                stroke="#6366f1"
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={{ r: 3, fill: "#6366f1" }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="avg_local"
                name="Map Pack"
                stroke="#38b2ac"
                strokeWidth={2.5}
                dot={{ r: 3.5, fill: "#38b2ac" }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
